/**
 * Edit wall's reach into the OTHER walls of the room. A region can belong to
 * a group that spans walls — a shower surround, a vanity's splash round a
 * corner, a kitchen backsplash — and a finish picked on, or a delete of, the
 * piece being edited carries to its group on those walls. Pure; the viewer
 * (`StructureViewerWrapper`) does the scene and store writes.
 *
 * Every rule here comes from the kitchen, where it broke (contractor call
 * items #28 / #9). A generated kitchen backsplash writes ONE `groupId` on every
 * piece of both of its bands — over the counter and behind the range — on
 * every wall. So:
 *
 * - a finish follows the group only to pieces of the SAME band
 *   (`isSameBacksplashBand`): the counter and the range band are tiled
 *   separately, as Fixtures & Finishes' two rows tile them;
 * - each linked piece takes the finish IN ITS OWN PLACE. The copy used to turn
 *   every piece on another wall into a copy of the first one it found, so a
 *   wall's three pieces — and its range band — stacked on one spot;
 * - the live preview redraws only those pieces. It used to clear EVERY tile
 *   on the other walls — their paint, their other band — and draw one;
 * - a delete never reaches another wall's kitchen backsplash. It used to take
 *   every band off every wall of the kitchen, with no warning.
 *
 * Saved regions (`texture.regions[]`) are matched; the focused region may be
 * in the editor's shape, which carries the same `surfaceType`, `groupId`,
 * `fixtureGroupId` and `kind`.
 */
import { isGeneratedKitchenBacksplash } from '@shared-lib/kitchen-assembly/styling/kitchenBacksplash';
import { clearUnchosenBacksplash } from '@shared-lib/kitchen-assembly/styling/unchosenBacksplash';
import { isSameBacksplashBand } from './wallRegionEdits';

const isShowerSurface = (r) =>
  r.surfaceType === 'showerWall' || r.source === 'shower';

/** The surface a link is judged by: its `surfaceType`, else its `source`. */
const surfaceOf = (r) => r?.surfaceType || r?.source || null;

/**
 * Which regions on other walls a finish picked on `focused` carries to, or
 * null when it carries nowhere. By group (`groupId` / `fixtureGroupId`), else
 * any shower tile for a shower region, else the same surface — and for a
 * backsplash, only the same band.
 */
export const linkedRegionMatcher = (focused) => {
  const linkId = focused?.groupId || focused?.fixtureGroupId;
  const st = surfaceOf(focused);
  let linked;
  if (linkId) {
    linked = (r) => r.groupId === linkId || r.fixtureGroupId === linkId;
  } else if (st === 'showerWall' || st === 'shower') {
    linked = isShowerSurface;
  } else if (st) {
    linked = (r) => r.surfaceType === st || r.source === st;
  } else {
    return null;
  }
  if (focused?.surfaceType !== 'backsplash') return linked;
  return (r) => linked(r) && isSameBacksplashBand(focused, r);
};

/** The finish as the saved shape writes it. */
const savedFinish = ({ textureId, paintData }) =>
  paintData
    ? { id: paintData.id, type: 'paint', color: paintData.color }
    : { id: textureId, type: 'texture' };

/**
 * `regions` with the finish on every region `matches` picks, each keeping its
 * own bounds and markers. A band that changes tile is a chosen one
 * (`clearUnchosenBacksplash`). Regions already wearing the finish are left by
 * identity, and `changed` lists the ones that moved, for the scene to redraw.
 */
export const retileLinkedRegions = (regions, matches, finish) => {
  const next = savedFinish(finish);
  const changed = [];
  const out = (regions || []).map((r) => {
    if (!matches(r)) return r;
    if (r.id === next.id && r.type === next.type) return r;
    const { color: _staleColor, ...rest } = r;
    const updated = clearUnchosenBacksplash({
      ...rest,
      id: next.id,
      type: next.type,
      ...(next.color ? { color: next.color } : {}),
    });
    changed.push(updated);
    return updated;
  });
  return { regions: out, changed };
};

/**
 * The key a region's group link is looked up by: its link id, narrowed to its
 * band for a backsplash piece that names one — the same rule as
 * `isSameBacksplashBand`, for the bird's-eye list, which previews a pick on
 * the other walls through a map of links.
 */
export const bandLinkKey = (linkId, region) =>
  region?.surfaceType === 'backsplash' && region.kind
    ? `${linkId}#${region.kind}`
    : linkId;

/** The regions on this wall a live preview redraws. */
export const linkedRegionsOnWall = (wall, matches) =>
  (wall?.texture?.regions || []).filter(matches);

/** Is this tile (by its texture metadata) one of these regions on this wall? */
export const isTileOfRegions = (texMeta, wallId, regionIds) =>
  !!texMeta?.parentWallIds?.includes(wallId) &&
  (regionIds || []).includes(texMeta.regionId);

/** The surfaces a delete in Edit wall carries to the other walls — a
 * fixture's tile, which stands round it on every wall it touches. */
const DELETED_ACROSS_WALLS = new Set([
  'showerWall',
  'tubBacksplash',
  'backsplash',
]);

/**
 * Does deleting this region in Edit wall also take its linked pieces off the
 * other walls? Only a fixture's tile does. A wainscot, whose walls share one
 * `groupId` from the Fixtures & Finishes apply, is deleted on its own wall.
 */
export const deletesAcrossWalls = (region) =>
  DELETED_ACROSS_WALLS.has(region?.surfaceType);

/**
 * The regions a delete of `deletedRegion` also takes off the OTHER walls, as
 * `[{ wallId, regionIds }]`; `[]` for a generated kitchen backsplash, whose
 * pieces share a group with the whole kitchen, and for anything that is not
 * deleted across walls (`deletesAcrossWalls`); null for an ungrouped region,
 * which the caller resolves by its fixture instead.
 */
export const groupDeleteTargets = (deletedRegion, walls, selectedWallId) => {
  const groupId = deletedRegion?.groupId;
  if (!groupId) return null;
  if (!deletesAcrossWalls(deletedRegion)) return [];
  if (isGeneratedKitchenBacksplash(deletedRegion)) return [];
  return (walls || [])
    .filter(
      (w) =>
        w.id !== selectedWallId &&
        w.texture?.regions?.some((r) => r.groupId === groupId)
    )
    .map((w) => ({
      wallId: w.id,
      regionIds: w.texture.regions
        .filter((r) => r.groupId === groupId)
        .map((r) => r.regionId),
    }));
};
